import { useState, useEffect } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useDemoState } from "@/data/demoState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Mail, FileText, Paperclip, Search, Filter, Eye, Tag, UserPlus, XCircle,
  ArrowRight, Info, Sparkles, Wifi, Clock, Users, RefreshCw, CheckCircle2
} from "lucide-react";

const typeIcons = {
  "Council request": { icon: FileText, color: "text-blue-600", bg: "bg-blue-50" },
  "Decision letter": { icon: FileText, color: "text-purple-600", bg: "bg-purple-50" },
  "Proof upload": { icon: Paperclip, color: "text-emerald-600", bg: "bg-emerald-50" },
  "Certificate": { icon: CheckCircle2, color: "text-teal-600", bg: "bg-teal-50" },
  General: { icon: Mail, color: "text-slate-500", bg: "bg-slate-100" },
};

export default function NewMail() {
  const { emailItems } = useDemoState();
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState(emailItems);
  const [search, setSearch] = useState("");

  useEffect(() => { setItems(emailItems); setSelected(null); }, [emailItems]);

  const filtered = items.filter(
    (e) => e.subject.toLowerCase().includes(search.toLowerCase()) ||
      e.project.toLowerCase().includes(search.toLowerCase()) ||
      e.senderName.toLowerCase().includes(search.toLowerCase())
  );

  const handleAction = (id, action) => {
    setItems((prev) => prev.map((e) => (e.id === id ? { ...e, status: action === "ignore" ? "processed" : "actioned" } : e)));
    if (action === "ignore") setSelected(null);
  };

  return (
    <AppLayout>
      <div className="space-y-4" data-testid="new-mail-page">
        {/* Page explainers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">What this page does</p>
                <p className="text-xs text-blue-600">Emails from Outlook or Gmail land here automatically. AI suggests what they are and where they belong.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">What happens next</p>
                <p className="text-xs text-slate-500">Your team reviews the classification and routes items to the right workflow: requests, decisions, or proof.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Users className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">Who uses this</p>
                <p className="text-xs text-slate-500">Builder PM, permit admin, office admin. Anyone managing incoming approvals correspondence.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Outlook connection bar */}
        <Card className="border-slate-200" data-testid="outlook-connection">
          <CardContent className="py-3 px-5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Wifi className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium text-slate-900">permits@builder.com.au</span>
              </div>
              <Badge variant="outline" className="text-[10px]">Outlook 365</Badge>
              <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Last sync: 2 min ago</span>
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Auto classification: On</Badge>
            </div>
            <Button variant="ghost" size="sm" className="gap-1.5 text-xs text-slate-500"><RefreshCw className="w-3 h-3" /> Sync now</Button>
          </CardContent>
        </Card>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mail-kpis">
          {[
            { label: "New emails this week", value: 12, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-100" },
            { label: "Council requests detected", value: 3, color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-100" },
            { label: "Decision letters detected", value: 1, color: "text-indigo-600", bg: "bg-indigo-50", border: "border-indigo-100" },
            { label: "Proof uploads detected", value: 4, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100" },
          ].map((s) => (
            <Card key={s.label} className={`${s.bg} ${s.border} border`}>
              <CardContent className="py-3 px-4">
                <p className="text-xs text-slate-500 mb-1">{s.label}</p>
                <p className={`text-2xl font-heading font-bold ${s.color}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* AI note */}
        <Card className="border-blue-100 bg-blue-50/40">
          <CardContent className="py-2.5 px-4 flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-blue-600 shrink-0" />
            <p className="text-xs text-blue-700">AI suggests classification and routing using sender, project address, permit number, and attachment content. Your team still reviews before it becomes active.</p>
          </CardContent>
        </Card>

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search emails..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="mail-search" />
          </div>
          <Button variant="outline" size="sm" className="gap-2 text-slate-600"><Filter className="w-3.5 h-3.5" /> Filter</Button>
        </div>

        {/* Email table */}
        <Card className="border-slate-200" data-testid="mail-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[120px]">Received</th>
                  <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Project</th>
                  <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sender</th>
                  <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Detected Type</th>
                  <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">AI Summary</th>
                  <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Suggested Owner</th>
                  <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[70px]">Confidence</th>
                  <th className="text-left py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[90px]">Status</th>
                  <th className="text-right py-2.5 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-[70px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((email) => {
                  const tInfo = typeIcons[email.detectedType] || typeIcons.General;
                  return (
                    <tr key={email.id} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors cursor-pointer ${selected?.id === email.id ? "bg-blue-50/30" : ""}`} onClick={() => setSelected(email)} data-testid={`mail-row-${email.id}`}>
                      <td className="py-3 px-4 text-xs text-slate-500">{email.receivedAt}</td>
                      <td className="py-3 px-4 text-sm font-medium text-slate-900">{email.project}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{email.senderName}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${tInfo.bg} ${tInfo.color}`}>
                          <tInfo.icon className="w-3 h-3" />{email.detectedType}
                        </span>
                        {email.itemsDetected > 0 && <span className="ml-1.5 text-[10px] text-slate-400">{email.itemsDetected} items</span>}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-500 max-w-[180px] truncate">{email.aiSummary}</td>
                      <td className="py-3 px-4 text-xs text-slate-600">{email.suggestedOwner || "Sarah Chen"}</td>
                      <td className="py-3 px-4"><span className="text-xs font-medium text-emerald-600">{email.confidence || "92%"}</span></td>
                      <td className="py-3 px-4"><StatusBadge status={email.status} size="sm" /></td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={(e) => { e.stopPropagation(); setSelected(email); }}><Eye className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Detail Sheet */}
        <Sheet open={!!selected} onOpenChange={() => setSelected(null)}>
          <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="mail-detail-sheet">
            {selected && (
              <>
                <SheetHeader>
                  <SheetTitle className="font-heading text-lg">{selected.subject}</SheetTitle>
                  <SheetDescription>From {selected.senderName} - {selected.receivedAt}</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Project</p>
                      <p className="text-sm font-medium text-slate-900">{selected.project}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Why this project</p>
                      <p className="text-xs text-slate-600">Matched by permit number and sender address.</p>
                    </div>
                  </div>
                  <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-blue-600" /><p className="text-xs font-semibold text-blue-700">AI Summary</p></div>
                    <p className="text-sm text-slate-700">{selected.aiSummary}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Suggested next owner</p>
                      <p className="text-sm text-slate-900">{selected.suggestedOwner || "Sarah Chen"}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Suggested next step</p>
                      <p className="text-xs text-slate-600">{selected.detectedType === "Council request" ? "Review request items" : selected.detectedType === "Decision letter" ? "Import and extract conditions" : "Review proof upload"}</p>
                    </div>
                  </div>
                  {selected.hasAttachments && (
                    <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                      <Paperclip className="w-4 h-4 text-slate-400" /><span className="text-sm text-slate-700">{selected.attachmentName}</span>
                      <Badge variant="outline" className="ml-auto text-[10px]">PDF</Badge>
                    </div>
                  )}
                  <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-600 leading-relaxed whitespace-pre-line max-h-48 overflow-y-auto">{selected.body}</div>
                  {selected.itemsDetected > 0 && (
                    <div className="bg-amber-50/50 border border-amber-100 rounded-lg p-3">
                      <p className="text-xs font-semibold text-amber-700">{selected.itemsDetected} {selected.detectedType === "Council request" ? "request items" : "conditions"} detected</p>
                      <p className="text-xs text-amber-600">Review and confirm before routing to workflows.</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button size="sm" className="bg-slate-900 text-white hover:bg-slate-800 gap-1.5" onClick={() => handleAction(selected.id, "review")} data-testid="mail-review-btn"><Eye className="w-3.5 h-3.5" /> Review {selected.detectedType === "Council request" ? "request" : selected.detectedType === "Decision letter" ? "decision" : "proof"}</Button>
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => handleAction(selected.id, "classify")} data-testid="mail-classify-btn"><Tag className="w-3.5 h-3.5" /> Reclassify</Button>
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => handleAction(selected.id, "assign")} data-testid="mail-assign-btn"><UserPlus className="w-3.5 h-3.5" /> Assign</Button>
                    <Button size="sm" variant="ghost" className="text-slate-400 gap-1.5" onClick={() => handleAction(selected.id, "ignore")} data-testid="mail-ignore-btn"><XCircle className="w-3.5 h-3.5" /> Mark as general</Button>
                  </div>
                  <div className="flex items-start gap-2 pt-3 border-t border-slate-100">
                    <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-slate-400">AI suggests. Humans approve. Review classifications before routing to workflows.</p>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
}
